// Pure rekenhulpen voor de speeltijdgrafiek (7/30 dagen): dagbuckets binnen/buiten, totalen,
// gemiddelde en reeks. Port van legacy/src/lib/playtime.ts, nu op LogEntry uit types.ts. Geen React.
import type { LogEntry } from './types';

export type RangeDays = 7 | 30;

/** Genormaliseerde sessie waar de grafiek mee rekent. */
export interface PlaySession {
  /** Lokale datum, yyyy-mm-dd. */
  dateKey: string;
  minutes: number;
  location: 'binnen' | 'buiten';
}

export interface DayPlaytimeData {
  dateKey: string;
  /** Korte as-label: "Vandaag"/"Ma" (7 dagen) of dagnummer "12" (30 dagen). */
  label: string;
  /** Bijv. "maandag 12 okt". */
  fullDate: string;
  binnen: number;
  buiten: number;
  total: number;
  count: number;
  isToday: boolean;
}

export interface PlaytimeStats {
  total: number;
  binnen: number;
  buiten: number;
  /** Dagen met minstens één missie. */
  activeDays: number;
  /** Gemiddelde minuten per dag over het hele bereik (afgerond). */
  avg: number;
  /** Hoogste dagtotaal. */
  max: number;
}

/** Bovengrens per sessie: alles boven 24 uur is vrijwel zeker corrupte data. */
const MAX_MINUTES_PER_SESSION = 24 * 60;

const DAY_SHORT = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const DAY_LONG = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** Lokale datumsleutel (yyyy-mm-dd). Bewust geen toISOString(): die rekent in UTC. */
export function toDateKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function parseMinutes(value: unknown): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim().replace(',', '.')) : NaN;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.round(n), MAX_MINUTES_PER_SESSION);
}

/** Eén logregel → sessie (lokale dag van `moment`), of null bij ongeldig moment/duur. */
export function toPlaySession(log: Pick<LogEntry, 'moment' | 'duurMinuten' | 'type'>): PlaySession | null {
  if (!log || (typeof log.moment !== 'string' && typeof log.moment !== 'number')) return null;
  const date = new Date(log.moment);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = parseMinutes(log.duurMinuten);
  if (minutes === 0) return null;
  const type = typeof log.type === 'string' ? log.type.trim().toLowerCase() : '';
  return { dateKey: toDateKey(date), minutes, location: type === 'buiten' ? 'buiten' : 'binnen' };
}

export function sessionsFromLogs(logs: readonly LogEntry[]): PlaySession[] {
  const out: PlaySession[] = [];
  for (const log of logs) {
    const s = toPlaySession(log);
    if (s) out.push(s);
  }
  return out;
}

/** Dagbuckets voor de laatste `range` dagen t/m vandaag (oudste eerst). */
export function buildDays(sessions: readonly PlaySession[], range: RangeDays, now: Date = new Date()): DayPlaytimeData[] {
  const byDay = new Map<string, { binnen: number; buiten: number; count: number }>();
  for (const s of sessions) {
    const bucket = byDay.get(s.dateKey) ?? { binnen: 0, buiten: 0, count: 0 };
    bucket[s.location] += s.minutes;
    bucket.count += 1;
    byDay.set(s.dateKey, bucket);
  }

  const today = startOfDay(now);
  const days: DayPlaytimeData[] = [];
  for (let i = range - 1; i >= 0; i--) {
    const d = addDays(today, -i);
    const key = toDateKey(d);
    const b = byDay.get(key) ?? { binnen: 0, buiten: 0, count: 0 };
    days.push({
      dateKey: key,
      label: range === 7 ? (i === 0 ? 'Vandaag' : DAY_SHORT[d.getDay()]) : String(d.getDate()),
      fullDate: `${DAY_LONG[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`,
      binnen: b.binnen,
      buiten: b.buiten,
      total: b.binnen + b.buiten,
      count: b.count,
      isToday: i === 0,
    });
  }
  return days;
}

/** Totalen en gemiddelde over een reeks dagbuckets (gemiddelde over álle dagen in het bereik). */
export function computeStats(days: readonly DayPlaytimeData[]): PlaytimeStats {
  let total = 0;
  let binnen = 0;
  let buiten = 0;
  let activeDays = 0;
  let max = 0;
  for (const d of days) {
    total += d.total;
    binnen += d.binnen;
    buiten += d.buiten;
    if (d.count > 0) activeDays += 1;
    max = Math.max(max, d.total);
  }
  return { total, binnen, buiten, activeDays, avg: days.length ? Math.round(total / days.length) : 0, max };
}

/**
 * Reeks van opeenvolgende dagen met minstens één missie. Als er vandaag nog niets
 * is gelogd, telt de reeks vanaf gisteren (vandaag is immers nog niet voorbij).
 */
export function computeStreak(sessions: readonly PlaySession[], now: Date = new Date()): number {
  const activeDays = new Set(sessions.map((s) => s.dateKey));
  let cursor = startOfDay(now);
  if (!activeDays.has(toDateKey(cursor))) cursor = addDays(cursor, -1);
  let streak = 0;
  while (activeDays.has(toDateKey(cursor))) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }
  return streak;
}

/** Grafiekopmaak: "0 min", "45 min", "1 u", "1 u 15 min". (Het logboek gebruikt logs.formatDuration.) */
export function formatPlaytime(mins: number): string {
  const total = Math.max(0, Math.round(Number(mins) || 0));
  if (total === 0) return '0 min';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} u` : `${h} u ${m} min`;
}

/** Ronde as-bovengrens in stappen van 15/30/60 minuten. */
export function niceAxisMax(maxMinutes: number): number {
  if (maxMinutes <= 0) return 60;
  const step = maxMinutes <= 60 ? 15 : maxMinutes <= 180 ? 30 : 60;
  return Math.ceil(maxMinutes / step) * step;
}
